import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "../../App";
import { setPostHogUser } from "../../config/posthog";

const getUserInfo = (userId) => {
  return axios
    .get(`${nuclearn_api_url}/user/info`, { params: { user_id: userId } })
    .then((response) => {
      return response.data;
    });
};

export const useUserInfo = (userId) => {
  return useQuery(["user", userId], () => getUserInfo(userId), {
    refetchOnWindowFocus: false,
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 5,
  });
};

export const getBillingInfo = (userId) => {
  return axios
    .get(`${nuclearn_api_url}/user/billing`, { params: { user_id: userId } })
    .then((response) => {
      return response.data;
    });
};

const adminResetUserOnboarding = (userId) => {
  return axios
    .post(`${nuclearn_api_url}/user/onboarding/admin/reset/${userId}`)
    .then((response) => response.data);
};

export const useAdminResetUserOnboarding = () => {
  const queryClient = useQueryClient();
  return useMutation(adminResetUserOnboarding, {
    onSuccess: () => {
      queryClient.invalidateQueries(["user"]);
      queryClient.invalidateQueries(["userProfile"]);
    },
  });
};
export class UserService {
  static get_user(user_id, callback, onErrorCallback) {
    // user level authorization route
    axios
      .get(nuclearn_api_url + "/user/info", { params: { user_id: user_id } })
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => onErrorCallback(error));
  }

  static get_billing_info(user_id) {
    axios
      .get(nuclearn_api_url + "/user/billing", {
        params: { user_id: user_id },
      })
      .then((response) => {
        return response.data;
      });
  }

  static get_all_users(callback) {
    // admin level route to get all user details
    axios.get(nuclearn_api_url + "/user/all").then((response) => {
      callback(response.data);
    });
  }

  static get_user_detail(user_id, callback, onErrorCallback) {
    // admin level route for all details about a user
    axios
      .get(nuclearn_api_url + "/user/detail", { params: { user_id: user_id } })
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => onErrorCallback(error));
  }

  static get_all_user_roles(callback) {
    // admin level route to get all user details
    axios.get(nuclearn_api_url + "/user/all-roles").then((response) => {
      callback(response.data);
    });
  }

  static getUserFromAuthZToken() {
    // token is injected by axios interceptor
    return axios
      .get(nuclearn_api_url + "/user/")
      .then((response) => {
        return response.data;
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  static setUserDataFromUserAuthZToken(onSuccess) {
    // sets the nuclearn user data session storage with received results from user data api after passed a nuclearn authorization token via axios interceptor
    this.getUserFromAuthZToken().then((userdata) => {
      localStorage.setItem("nuclearn_user", JSON.stringify(userdata));
      setPostHogUser();
      onSuccess(userdata);
    });
  }
}

const getApiKey = () => {
  // retrieves the api key from the server
  return axios.get(nuclearn_api_url + "/auth/api-key").then((response) => {
    return response.data;
  });
};

export const useGetApiKey = (enabled) => {
  return useQuery("apiKey", () => getApiKey(), { enabled: enabled });
};

const resetApiKey = () => {
  return axios
    .post(nuclearn_api_url + "/auth/api-key/reset")
    .then((response) => {
      return response.data;
    });
};

export const useResetApiKey = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(resetApiKey, {
    onSuccess: () => {
      queryClient.invalidateQueries("apiKey");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getPasswordResetToken = (username) => {
  // retrieves a password reset link from the server
  return axios
    .post(`${nuclearn_api_url}/auth/password/reset-request-token`, {
      username: username,
    })
    .then((response) => {
      return response.data;
    });
};

export const useGetPasswordResetToken = (username, enabled) => {
  return useQuery(
    ["passwordResetToken", username],
    () => getPasswordResetToken(username),
    { enabled: enabled },
  );
};

const updateUserRole = (data) => {
  return axios
    .patch(`${nuclearn_api_url}/user/update-role`, data)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      const message = error?.response?.data?.detail;
      throw message;
    });
};

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateUserRole, {
    onSuccess: () => {
      queryClient.invalidateQueries("user");
    },
  });
  return mutation;
};

const getAllUserPrivileges = () => {
  return axios
    .get(`${nuclearn_api_url}/user/role-privileges`)
    .then((response) => {
      return response.data;
    });
};

export const useGetAllUserPrivileges = () => {
  return useQuery("userRolesPriv", () => getAllUserPrivileges(), {
    refetchOnWindowFocus: false,
  });
};

const getAllUserRoles = () => {
  return axios.get(`${nuclearn_api_url}/user/roles/all`).then((response) => {
    return response.data;
  });
};

export const useGetAllUserRoles = () => {
  return useQuery(["userRoles"], () => getAllUserRoles(), {
    refetchOnWindowFocus: false,
  });
};

const getUserDeletableRoles = () => {
  return axios
    .get(`${nuclearn_api_url}/user/roles/deletable`)
    .then((response) => {
      return response.data;
    });
};

export const useGetUserDeletableRoles = () => {
  return useQuery(
    ["userRoles", "userDeletableRoles"],
    () => getUserDeletableRoles(),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const updateRolesAndPrivileges = (data) => {
  return axios
    .put(`${nuclearn_api_url}/user/role-privileges`, data)
    .then((response) => {
      return response.data;
    });
};

export const useUpdateRolesAndPrivileges = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateRolesAndPrivileges, {
    onSuccess: () => {
      queryClient.invalidateQueries("userRolesPriv");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const createRole = (data) => {
  return axios.post(`${nuclearn_api_url}/user/role`, data).then((response) => {
    return response.data;
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(createRole, {
    onSuccess: () => {
      queryClient.invalidateQueries("userRolesPriv");
      queryClient.invalidateQueries("userRoles");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const deleteRoles = (data) => {
  return axios
    .delete(`${nuclearn_api_url}/user/role`, {
      data: { roles: data },
    })
    .then((response) => {
      return response.data;
    });
};

export const useDeleteRoles = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteRoles, {
    onSuccess: () => {
      queryClient.invalidateQueries("userRolesPriv");
      queryClient.invalidateQueries("userRoles");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const updateUserEmailValidated = (data) => {
  return axios
    .patch(`${nuclearn_api_url}/user/update-email-validated`, data)
    .then((response) => {
      return response.data;
    });
};

export const useUpdateUserEmailValidated = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateUserEmailValidated, {
    onSuccess: () => {
      queryClient.invalidateQueries("user");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};
